import { notifications } from '@mantine/notifications'
import { CredentialResponse } from '@react-oauth/google'
import { jwtDecode } from 'jwt-decode'
import ky from 'ky'
import { createContext, PropsWithChildren, useEffect, useState } from 'react'

import { API_HOST } from '@/utils/constants'

import { RoleGroup } from '../models'
import { endpoints } from '../types/endpoints'

export const JWT_KEY = 'JWT'

let currentJwt: string | undefined = undefined

export const getJwt = () => {
  return currentJwt || localStorage.getItem(JWT_KEY) || undefined
}

interface ApiAuthPayload {
  userId: string
  roles: RoleGroup
  iat: number
  userName?: string
  branchId?: string
}

export interface AuthContextType {
  jwt?: string
  isAuthenticated: boolean
  userRoles: RoleGroup
  isLoading: boolean
  userName?: string
  userId?: string
  branchId?: string
  matchRoles: (authorizedRoles: RoleGroup) => boolean
  onGoogleSuccess: (credentialResponse: CredentialResponse) => Promise<void>
  onSignOut: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const decodeToken = (token: string | null): ApiAuthPayload | null => {
  try {
    if (!token) return null
    return jwtDecode<ApiAuthPayload>(token)
  } catch (error) {
    console.error('Token decoding failed', error)
    return null
  }
}

export const AuthProvider = (props: PropsWithChildren) => {
  const [jwt, setJwt] = useState<string | undefined>(undefined)
  const [userRoles, setUserRoles] = useState<RoleGroup>([])
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [userName, setUserName] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [branchId, setBranchId] = useState<string | undefined>(undefined)
  useEffect(() => {
    const token = localStorage.getItem(JWT_KEY)
    const decoded = decodeToken(token)
    if (decoded) {
      setJwt(token ?? undefined)
      setUserId(decoded.userId)
      setUserRoles(decoded.roles)
      setUserName(decoded.userName)
      setIsAuthenticated(true)
      setBranchId(decoded.branchId)
      currentJwt = token as string
    }
    setIsLoading(false)
  }, [])

  const onGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      const data: any = await ky
        .post(`${API_HOST}${endpoints.ACCESS_TOKEN}`, {
          json: { idToken: credentialResponse.credential },
        })
        .json()

      const { token } = data
      const decoded = decodeToken(token)

      if (decoded) {
        setJwt(token)
        setUserId(decoded.userId)
        setUserRoles(decoded.roles)
        setUserName(decoded.userName)
        setBranchId(decoded.branchId)
        setIsAuthenticated(true)
        localStorage.setItem(JWT_KEY, token)
        currentJwt = token
      }
    } catch (error: any) {
      switch (error.response?.status) {
        case 404:
          notifications.show({
            title: 'User not found',
            message: 'Please contact your administrator',
            color: 'red',
          })
          break
        case 403:
          notifications.show({
            title: 'User is not authorized to access this application',
            message: 'Please contact your administrator',
            color: 'red',
          })
          break
        default:
          notifications.show({
            title: 'Google Sign-in failed due to an unexpected error.',
            message: 'Please contact your administrator',
            color: 'red',
          })
          break
      }
    }
  }

  const onSignOut = () => {
    localStorage.removeItem(JWT_KEY)
    setJwt(undefined)
    setUserRoles([])
    setUserName(undefined)
    setUserId(undefined)
    setBranchId(undefined)
    setIsAuthenticated(false)
    currentJwt = undefined
  }

  const matchRoles = (authorizedRoles: RoleGroup) => {
    return isAuthenticated && (authorizedRoles.length === 0 || userRoles.some((role) => authorizedRoles.includes(role)))
  }

  return (
    <AuthContext.Provider
      value={{
        jwt,
        isAuthenticated,
        userRoles,
        userId,
        userName,
        branchId,
        onGoogleSuccess,
        onSignOut,
        matchRoles,
        isLoading,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  )
}
